<template>
  <content-modal
    ref="fileUploadModal"
    :visible="visible"
    :close-aria-label="t('modal_close_aria_label')"
    :size="size"
    :auto-hide-off="autoHideOff"
    :disable-close="isLoading"
    class="file-uploader-modal"
    @modal-hide-request="hideRequest"
  >
    <template #title>{{ t("file_upload_label_upload_file") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <file-uploader-v2
            ref="fileUpload"
            :added-file="params.addedFile"
            :dirty-id="dirtyId"
            :upload-flow="uploadFlow"
            :cropping-configuration="croppingConfiguration"
            :dimension-message-type="dimensionMessageType"
            @update:file-entry="handleFileResponse"
            @file-changed="handleFileChanged"
          />
        </template>
        <template #tools>
          <cv-button class="cancel" kind="tertiary" size="field" :disabled="isLoading" @click="hide"
            >{{ t("button_assignment_cancel") }}
          </cv-button>
          <fetch-data-button
            :kind="dataUploadButton.kind"
            :size="dataUploadButton.size"
            :loading="isLoading"
            :disabled="isLoading || dataUploadButton.error"
            :error="dataUploadButton.error"
            :tip-position="dataUploadButton.tipPosition"
            @click="handleUpload"
            @reset-error-state="handleUploadButtonResetError"
          >
            <span v-if="dataUploadButton.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ t("button_upload") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import randomUtils from "@utils/random.utils";
import { computed, onBeforeUnmount, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { useDirty } from "@plugins/common/composables/page-dirty";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import { FileResponse, FileStatus, FileUploadEntry } from "@plugins/file-uploader-v2/types.ts";

// Props
const props = defineProps({
  params: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["update:fileEntry", "fileChanged"]);

// Template Refs
const fileUploadRef = useTemplateRef("fileUpload");

// Data
const id = ref(randomUtils.randomId());
const size = ref("medium");
const autoHideOff = ref(true);
const visible = ref(true);
const uploadFlow = ref(props.params.uploadFlow);
const isLoading = ref(false);
const fileEntry = ref<FileUploadEntry | null>(null);
const fileResponse = ref<FileResponse | null>(null);

const dataUploadButton = ref({
  tipPosition: "top",
  kind: "primary",
  size: "field",
  error: false,
});

const { t } = useI18n();
const modal = useModal();

const dirtyId = computed(() => id.value);
const { setDirty, removeDirty, promptComponentDirty } = useDirty(dirtyId);

// Computed
const croppingConfiguration = computed(() => props.params?.croppingConfiguration);
const dimensionMessageType = computed(() => props.params?.dimensionMessageType);

// Methods
const handleUpload = () => {
  if (isLoading.value) {
    // do nothing when clicking on upload button while still uploading
    return;
  }
  if (fileEntry.value == null) {
    fileUploadRef.value?.setErrorMessage(t("file_upload_validation_empty_file_message"));
  } else if (fileResponse.value != null && fileResponse.value.status === FileStatus.READY) {
    emit("update:fileEntry", fileResponse.value);
    hide();
  }
  isLoading.value = false;
};

const handleUploadButtonResetError = () => {
  dataUploadButton.value.error = false;
};

const hide = () => {
  // restore previous scrollability
  DOMUtils.removeClass("body", "bx--body--with-modal-open");
  removeDirty(id.value);
  modal.remove(props.params.modalId);
};

const hideRequest = (event) => {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    promptComponentDirty().then((response: boolean) => {
      if (!response) return;
      hide();
    });
  }
};

const handleFileResponse = (data: FileResponse) => {
  setDirty(id.value);
  fileResponse.value = data;
  if (fileResponse.value) {
    isLoading.value = false;
  } else {
    isLoading.value = true;
  }
};

const handleFileChanged = (data) => {
  isLoading.value = true;
  fileEntry.value = data;
  if (fileEntry.value == null) {
    isLoading.value = false;
  }
  emit("fileChanged", data);
};

onBeforeUnmount(() => {
  hide();
});
</script>
